import { useRef } from "react";
import { motion } from "framer-motion";
import { Mic2, Coffee, BookOpen } from "lucide-react";
import { CINEMAS_SPINE_URL } from "@/data/content";
import { Reveal } from "@/components/bits/Reveal";
import { Sticker, Tape, HandNote } from "@/components/bits/Sticker";

const Drag = ({ children, constraints, className = "", rotate = 0, delay = 0, testId }) => (
  <motion.div
    data-testid={testId}
    data-hover
    className={`absolute cursor-grab active:cursor-grabbing ${className}`}
    drag
    dragConstraints={constraints}
    dragElastic={0.2}
    dragMomentum={false}
    initial={{ opacity: 0, scale: 0.7, rotate: rotate - 10 }}
    whileInView={{ opacity: 1, scale: 1, rotate }}
    whileHover={{ scale: 1.04, zIndex: 20 }}
    whileDrag={{ scale: 1.06, zIndex: 30, rotate: 0 }}
    viewport={{ once: true, margin: "-10% 0px" }}
    transition={{ type: "spring", stiffness: 200, damping: 18, delay }}
  >
    {children}
  </motion.div>
);

export const Playground = () => {
  const box = useRef(null);

  return (
    <section id="playground" className="relative max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="playground-section">
      <Reveal className="text-center mb-10">
        <HandNote className="text-2xl block">playground · drag things around</HandNote>
        <h2 className="pixel-title text-5xl sm:text-6xl md:text-8xl mt-1">Fun Experiment</h2>
      </Reveal>

      <div ref={box} className="relative h-[620px] md:h-[640px] border-[1.5px] border-dashed border-[var(--ink-30)] rounded-xl" data-testid="playground-board">
        <Drag constraints={box} className="left-[3%] top-[6%] w-40 md:w-52" rotate={-7} testId="drag-shot-0">
          <div className="polaroid" style={{ "--r": "0deg" }}>
            <Tape rotate={-8} className="-top-3 left-6" />
            <img src={`${process.env.PUBLIC_URL || ""}/images/img-7306.jpg`} alt="Person walking along a tree-lined road with cattle" />
            <HandNote className="absolute bottom-2 left-3 text-lg">Recce in Rajasthan</HandNote>
          </div>
        </Drag>

        <Drag constraints={box} className="right-[4%] top-[4%] w-44 md:w-56" rotate={6} delay={0.1} testId="drag-shot-1">
          <div className="polaroid wide" style={{ "--r": "0deg" }}>
            <Tape rotate={6} className="-top-3 right-6" color="blue" />
            <img src={`${process.env.PUBLIC_URL || ""}/images/img-5241.jpg`} alt="Outdoor steps and colorful flags at DIFF 2025" />
            <HandNote className="absolute bottom-2 left-3 text-lg">DIFF 2025</HandNote>
          </div>
        </Drag>

        <Drag constraints={box} className="left-[30%] top-[28%] md:left-[36%] md:top-[22%]" rotate={-3} delay={0.15} testId="drag-hello">
          <div className="bg-white border-[1.5px] border-[var(--ink)] hard-shadow px-8 py-6 w-56 md:w-72">
            <div className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[var(--ink-60)]">terminal · macintosh</div>
            <div className="font-hand text-5xl md:text-6xl mt-2 leading-none">hello.</div>
            <div className="font-mono text-[0.65rem] mt-3 text-[var(--ink-60)]">&gt; coffee_from_sachet.exe <span className="animate-pulse">▍</span></div>
          </div>
        </Drag>

        <Drag constraints={box} className="left-[6%] bottom-[10%] md:left-[10%] md:bottom-[14%]" rotate={4} delay={0.2} testId="drag-podcast">
          <a href={CINEMAS_SPINE_URL} target="_blank" rel="noopener noreferrer">
            <div className="bg-[var(--purple)] border-[1.5px] border-[var(--ink)] hard-shadow p-5 w-52 rounded-full aspect-square flex flex-col items-center justify-center text-center">
              <Mic2 size={22} />
              <div className="font-pixel text-2xl mt-2 leading-none">Cinema's Spine</div>
              <div className="font-mono text-[0.6rem] mt-2 tracking-[0.15em] uppercase">a podcast on set stories</div>
            </div>
          </a>
        </Drag>

        <Drag constraints={box} className="right-[8%] bottom-[8%] w-40 md:w-52 md:right-[12%]" rotate={-5} delay={0.25} testId="drag-shot-2">
          <div className="polaroid" style={{ "--r": "0deg" }}>
            <Tape rotate={-4} className="-top-3 left-1/2 -translate-x-1/2" color="pink" />
            <img src={`${process.env.PUBLIC_URL || ""}/images/img-2260.jpg`} alt="Woman holding a drink at Joseph Bar" />
            <HandNote className="absolute bottom-2 left-3 text-lg">Joseph Bar, 2026</HandNote>
          </div>
        </Drag>

        <Drag constraints={box} className="left-[40%] bottom-[6%] md:left-[44%] md:bottom-[12%]" rotate={2} delay={0.3} testId="drag-coffee">
          <Sticker color="yellow" icon={<Coffee size={12} />} className="!py-3 !px-4 !text-xs">coffee · sachet · tiny bisleri bottle</Sticker>
        </Drag>

        <Drag constraints={box} className="left-[22%] top-[62%] md:left-[32%] md:top-[58%]" rotate={5} delay={0.4} testId="drag-books">
          <Sticker color="green" icon={<BookOpen size={12} />} className="!py-3 !px-4 !text-xs">reads & breaks down scripts</Sticker>
        </Drag>

        <span className="absolute bottom-3 right-4 font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[var(--ink-30)]">fig. 04 · the desk</span>
      </div>
    </section>
  );
};
